import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BiSolidCommentDetail, BiSolidLike, BiTrash } from "react-icons/bi";
import { FaUserCircle } from "react-icons/fa";
import { HiOutlineArrowUpRight } from "react-icons/hi2";
import { TbBriefcase, TbSchool, TbClock, TbCheck, TbX, TbPencil } from "react-icons/tb";
import UserAvatar from '../UserAvatar/UserAvatar';
import FormatDate from '../CommentCard/FormatDate';
import { useSelector, useDispatch } from 'react-redux';
import PopupComp from '../Popups/PopupComp';
import { deletePost } from '../../Actions/jobs';

const PALETTES = [
  { bg: '#EFF6FF', text: '#1D4ED8', border: '#BFDBFE' },
  { bg: '#F5F3FF', text: '#6D28D9', border: '#DDD6FE' },
  { bg: '#ECFDF5', text: '#065F46', border: '#A7F3D0' },
  { bg: '#FFF7ED', text: '#9A3412', border: '#FED7AA' },
  { bg: '#FFF1F2', text: '#9F1239', border: '#FECDD3' },
  { bg: '#F0F9FF', text: '#0369A1', border: '#BAE6FD' },
  { bg: '#FEFCE8', text: '#854D0E', border: '#FEF08A' },
  { bg: '#F0FDF4', text: '#166534', border: '#BBF7D0' },
];

function CompanyAvatar({ name }) {
  const initials = name ? name.trim().split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('') : '?';
  const safeNameCode = name?.charCodeAt(0) ?? 0;
  const index = PALETTES.length > 0 ? safeNameCode % PALETTES.length : 0;
  const p = PALETTES[index] || PALETTES[0]; 

  return (
    <div
      style={{ background: p.bg, color: p.text, border: `1px solid ${p.border}` }}
      className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center font-black text-sm flex-shrink-0 tracking-tight shadow-sm"
    >
      {initials}
    </div>
  );
}

function OutcomeChip({ status }) {
  const ok = status === 'Selected';
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] sm:text-[10px] font-black uppercase tracking-wider border ${
      ok ? 'bg-emerald-50/80 border-emerald-200 text-emerald-700' : 'bg-rose-50/80 border-rose-200 text-rose-600'
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full shadow-sm ${ok ? 'bg-emerald-500' : 'bg-rose-500'}`} />
      {status}
    </div>
  );
}

function PostCard({ post }) {
  const { user } = useSelector(s => s.user);
  const dispatch = useDispatch();
  const isOwner = user && post?.owner?._id === user?._id;
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [liked, setLiked] = useState(false);

  const handleDelete = () => {
    dispatch(deletePost(post._id));
    setShowDeletePopup(false);
  };

  return (
    <>
      {/* Glassmorphism Container:
        - bg-white/70 to bg-white/40 creates the frosted base
        - backdrop-blur-xl creates the glass distortion
        - ring-1 ring-white/60 gives it that shiny edge
      */}
      <div className="group relative rounded-2xl bg-gradient-to-br from-white/80 to-white/40 backdrop-blur-xl ring-1 ring-white/60 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(59,130,246,0.12)] hover:-translate-y-0.5 hover:bg-white/90 transition-all duration-300 overflow-hidden">
        
        <div className="p-4 sm:p-5">
          {/* ━━━━ BLOCK 1: Header ━━━━ */}
          <div className="flex justify-between items-start mb-3 gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <CompanyAvatar name={post.company} />
              <div className="min-w-0">
                <h3 className="font-extrabold text-slate-900 text-[15px] sm:text-base leading-tight truncate">
                  {post.company}
                </h3>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <span className="flex items-center gap-1 text-[10px] font-semibold text-slate-500 bg-slate-100/80 px-1.5 py-0.5 rounded">
                    <TbBriefcase size={11} /> {post.type}
                  </span>
                  {post.collegeName && (
                    <span className="flex items-center gap-1 text-[10px] font-semibold text-indigo-600 bg-indigo-50/80 px-1.5 py-0.5 rounded">
                      <TbSchool size={11} /> {post.collegeName}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <OutcomeChip status={post.status} />
          </div>

          {/* ━━━━ BLOCK 2: Title ━━━━ */}
          <Link to={`/post/${post._id}`} className="block mb-4">
            <h2 className="text-[15px] sm:text-[17px] font-bold text-slate-800 leading-snug group-hover:text-blue-600 transition-colors line-clamp-2">
              {post.caption}
            </h2>
          </Link>

          {/* ━━━━ BLOCK 3: Footer (Author & Actions) ━━━━ */}
          <div className="flex items-center justify-between pt-3.5 border-t border-slate-200/60">
            
            {/* Author Info with Branch & Batch */}
            <Link to={`/user/${post?.owner?._id}`} className="flex items-center gap-2.5 min-w-0 group/user">
              {post?.owner?.profileImage ? (
                <img src={post.owner.profileImage} alt="User" className="w-8 h-8 rounded-full object-cover ring-2 ring-white" />
              ) : (
                <FaUserCircle size={32} className="text-slate-300 group-hover/user:text-blue-400 transition-colors" />
              )}
              <div className="min-w-0">
                <p className="text-[12px] font-bold text-slate-800 truncate group-hover/user:text-blue-600 transition-colors">
                  {post?.owner?.name}
                </p>
                
                {/* Re-added details with clean dot separators */}
                <div className="flex items-center text-[9px] sm:text-[10px] font-medium text-slate-400 mt-0.5 gap-1.5 flex-wrap">
                  {post?.owner?.branch && <span className="truncate max-w-[80px]">{post.owner.branch}</span>}
                  
                  {post?.owner?.branch && post?.owner?.graduationYear && (
                    <span className="w-0.5 h-0.5 rounded-full bg-slate-300" />
                  )}
                  
                  {post?.owner?.graduationYear && <span>{post.owner.graduationYear}</span>}
                  
                  <span className="w-0.5 h-0.5 rounded-full bg-slate-300" />
                  
                  <span className="flex items-center gap-0.5 text-slate-400 whitespace-nowrap">
                    <TbClock size={10} /> {FormatDate(post.createdAt)}
                  </span>
                </div>
              </div>
            </Link>

            {/* Actions */}
            <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
              <button 
                onClick={() => setLiked(!liked)} 
                className={`flex items-center gap-1 px-2 sm:px-2.5 py-1.5 rounded-xl text-[11px] font-bold transition-all ${
                  liked ? 'text-orange-600 bg-orange-50' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50/50'
                }`}
              >
                <BiSolidLike size={14} /> {post?.likesCount ?? 0}
              </button>
              
              <Link 
                to={`/post/${post._id}#discussion`} 
                className="flex items-center gap-1 px-2 sm:px-2.5 py-1.5 rounded-xl text-[11px] font-bold text-slate-400 hover:text-blue-600 hover:bg-blue-50/50 transition-all"
              >
                <BiSolidCommentDetail size={14} /> {post?.commentsCount ?? 0}
              </Link>

              {isOwner && (
                <div className="flex border-l border-slate-200/60 pl-1 sm:pl-1.5 ml-0.5">
                  <button onClick={() => setShowDeletePopup(true)} className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all">
                    <BiTrash size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── DELETE POPUP ── */}
      {showDeletePopup && (
        <PopupComp onClose={() => setShowDeletePopup(false)}>
           <div className="bg-white p-6 sm:p-8 rounded-3xl text-center max-w-sm mx-auto shadow-2xl">
            <div className="w-12 h-12 bg-rose-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-rose-100">
              <BiTrash size={22} className="text-rose-500" />
            </div>
            <h3 className="text-lg font-black text-slate-900 mb-1.5">Delete this post?</h3>
            <p className="text-slate-500 text-xs sm:text-sm mb-5">This action cannot be undone.</p>
            <div className="flex flex-col gap-2">
              <button onClick={handleDelete} className="w-full py-3 rounded-xl font-bold text-white bg-rose-600 hover:bg-rose-700 transition-all">
                Yes, Delete
              </button>
              <button onClick={() => setShowDeletePopup(false)} className="w-full py-3 rounded-xl font-semibold text-slate-500 hover:bg-slate-50 transition-all">
                Cancel
              </button>
            </div>
          </div>
        </PopupComp>
      )}
    </>
  );
}

export default PostCard;